// 3rd Party
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { GoogleMap, withGoogleMap } from 'react-google-maps';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

// Components
import { TextSkeleton } from 'common/styles/oc-styled-components';

// Other
import { colors } from 'common/styles/style-constants';
import { updateSelectedResult } from 'connect/actions';
import {
  makeSelectClinicMarker,
  makeSelectResultsMapMarkers,
  selectDataLatitude,
  selectDataLongitude,
  selectDataSelectedResultIndex,
} from 'connect/selectors';
import { HighlightedResultStateHandler } from 'connect/state/handlers/results';
import {
  DIMENSIONS,
  useWindowDimensions,
} from 'providers/WindowDimensionsProvider';
import loadScript from 'utils/loadScript';

import { useFunnelStorage } from '../../../funnel/storage/Storage';
import { STEP_KEY } from '../../../funnel/utils';
import {
  GOOGLE_MAPS_URL,
  GOOGLE_SCRIPT_ID,
  MAP_OPTIONS,
} from '../../constants/google-maps-constants';
import {
  getMapBounds,
  getResultBounds,
  getUserLocationMapMarker,
} from '../../helpers/google-maps-helpers';

import MapMarker from './MapMarker';

// Styles (note: these are not styled components)
const containerElementStyles = {
  display: 'flex', // Required for Safari
  flex: '1 1 auto',
  flexDirection: 'column', // Required for Safari
};

const containerHeaderStyled = {
  display: 'flex', // Required for Safari
  flex: '1 1 auto',
  flexDirection: 'column', // Required for Safari
  width: '100%',
  height: '200px',
};

const loadingElementStyles = {
  height: '100%',
};

const mapElementStyles = {
  border: `1px solid ${colors.silver}`,
  borderRadius: '3px',
  flex: '1 1 auto', // Required for Safari
  height: '100%',
};

// Internal
const GoogleMapsInner = withGoogleMap(
  ({ markers, fallbackPosition, hasInteraction }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const highlightedResultIndex = useSelector(
      HighlightedResultStateHandler.selector,
    );
    const selectedResultIndex = useSelector(selectDataSelectedResultIndex);

    const hasHighlightedResult = typeof highlightedResultIndex === 'number';
    const hasSelectedResult = typeof selectedResultIndex === 'number';

    const mapBounds = getMapBounds(markers, fallbackPosition);

    const [mapRef, setMapRef] = useState();
    const [userHasZoomed, setUserHasZoomed] = useState(false);
    const { layout, height, width } = useWindowDimensions();

    const isDesktop =
      layout === DIMENSIONS.DESKTOP || layout === DIMENSIONS.WIDESCREEN;
    // On map update
    useEffect(() => {
      if (mapRef && !userHasZoomed) {
        const resultMarkers = markers.filter(
          marker => marker.id !== 'user-location',
        );

        if (
          !hasSelectedResult && !hasHighlightedResult
          || resultMarkers.length === 1
        ) {
          mapRef.fitBounds(mapBounds);
        } else if (hasSelectedResult && !mapRef.getBounds()) {
          const resultBounds = getResultBounds(
            mapBounds,
            markers[markers.length === 1 ? 0 : selectedResultIndex],
          );
          mapRef.fitBounds(resultBounds);
        }
      }
    }, [height, mapRef, width, mapBounds]);

    // On result selection
    useEffect(() => {
      if (mapRef && hasSelectedResult) {
        mapRef.panTo(markers[selectedResultIndex].position);
      }
    }, [selectedResultIndex]);

    const onZoomChanged = () => {
      setUserHasZoomed(true);
    };

    const highlightMarker = resultIndex =>
      dispatch(HighlightedResultStateHandler.update(resultIndex));

    const selectMarker = selectedIndex => {
      if (isDesktop) {
        history.push(`/results/${markers[selectedIndex].id}`);
      } else {
        dispatch(updateSelectedResult(selectedIndex));
      }
    };

    return (
      <GoogleMap
        defaultOptions={MAP_OPTIONS}
        ref={map => setMapRef(map)}
        onZoomChanged={onZoomChanged}
      >
        {markers.map((marker, i) => (
          <MapMarker
            hasInteraction={hasInteraction}
            highlighted={i === highlightedResultIndex}
            index={i}
            key={marker.id}
            marker={marker}
            onHighlight={highlightMarker}
            onSelect={selectMarker}
            selected={i === selectedResultIndex}
            map={mapRef}
          />
        ))}
      </GoogleMap>
    );
  },
);

// Component
const GoogleMaps = ({ singleMarker }) => {
  const storage = useFunnelStorage();
  const userLocationData = storage.get(STEP_KEY.LOCATION);

  const [scriptIsLoaded, setScriptIsLoaded] = useState(false);
  const clinicMarker = useSelector(makeSelectClinicMarker);
  const multiMarkers = useSelector(makeSelectResultsMapMarkers);
  const userLocationMarker = userLocationData
    ? getUserLocationMapMarker(userLocationData)
    : [];

  const matchPosition = {
    lat: useSelector(selectDataLatitude),
    lng: useSelector(selectDataLongitude),
  };

  const markers = [
    ...userLocationMarker,
    ...singleMarker ? clinicMarker : multiMarkers,
  ];

  const loading = !markers && !markers[0].id;

  useEffect(() => {
    loadScript(GOOGLE_MAPS_URL, GOOGLE_SCRIPT_ID, () =>
      setScriptIsLoaded(true) );
  }, []);

  // Absolutely do not attempt to load Google Map until script is initialized
  if (!scriptIsLoaded || !window.google) {
    return null;
  }

  return (
    <TextSkeleton
      active
      height="100%"
      loading={loading}
      margin="0"
      paragraph={false}
      title
      width="100%"
    >
      <GoogleMapsInner
        containerElement={(
          <div
            style={
              singleMarker ? containerHeaderStyled : containerElementStyles
            }
          />
        )}
        googleMapURL={GOOGLE_MAPS_URL}
        loadingElement={<div style={loadingElementStyles} />}
        mapElement={<div id="map" style={mapElementStyles} />}
        markers={markers}
        hasInteraction={!singleMarker}
        fallbackPosition={matchPosition}
      />
    </TextSkeleton>
  );
};

GoogleMaps.propTypes = {
  singleMarker: PropTypes.bool,
};

export default GoogleMaps;
